import React from "react";
import { BiCheck, BiMinus } from "react-icons/bi";
import { PLANS, PLAN_ORDER, formatLimit, formatPrice } from "./plans";

type Cell = boolean | string;

const groups: { title: string; rows: { label: string; values: Cell[] }[] }[] = [
  {
    title: "Price",
    rows: [
      {
        label: "Monthly",
        values: PLAN_ORDER.map((key) => formatPrice(PLANS[key].monthlyPrice)),
      },
      {
        label: "Yearly",
        values: PLAN_ORDER.map((key) => formatPrice(PLANS[key].annualPrice)),
      },
    ],
  },
  {
    title: "Limits",
    rows: [
      {
        label: "Active events",
        values: PLAN_ORDER.map((key) => formatLimit(PLANS[key].limits.events)),
      },
      {
        label: "Inventory items",
        values: PLAN_ORDER.map((key) => formatLimit(PLANS[key].limits.items)),
      },
    ],
  },
  {
    title: "Selling",
    rows: [
      { label: "Booth checkout", values: [true, true, true] },
      { label: "Square card payments", values: [true, true, true] },
      { label: "Cash, manual, and QR payments", values: [true, true, true] },
      { label: "Sales tracking by event", values: [true, true, true] },
    ],
  },
  {
    title: "Inventory & team",
    rows: [
      { label: "Multiple inventory pools", values: [false, false, true] },
      { label: "Employees", values: [false, false, true] },
      { label: "Staff pay and commission", values: [false, false, true] },
    ],
  },
  {
    title: "Finance",
    rows: [
      { label: "Finance overview + expenses", values: [true, true, true] },
      { label: "Advanced finance analytics", values: [false, false, true] },
    ],
  },
  {
    title: "Support",
    rows: [
      { label: "Support", values: ["Community", "Email", "Priority"] },
    ],
  },
];

const renderCell = (value: Cell, isPro: boolean) => {
  if (value === true) {
    return (
      <BiCheck
        size={20}
        aria-label="Included"
        className={`mx-auto ${isPro ? "text-violet-600" : "text-blue-600"}`}
      />
    );
  }
  if (value === false) {
    return (
      <BiMinus
        size={18}
        aria-label="Not included"
        className="mx-auto text-slate-300"
      />
    );
  }
  return value;
};

const PlanComparison: React.FC = () => (
  <section className="bg-white px-4 py-20 md:px-8 md:py-28">
    <div className="mx-auto max-w-5xl">
      <div className="mx-auto max-w-2xl text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-600">
          Compare plans
        </p>
        <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 md:text-5xl">
          Everything side by side
        </h2>
      </div>

      <div className="mt-12 overflow-hidden rounded-3xl border border-slate-200 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.25)]">
        <table className="w-full table-fixed text-[13px] md:text-base">
          <colgroup>
            <col className="w-[34%] md:w-[46%]" />
            <col />
            <col />
            <col className="bg-violet-50/60" />
          </colgroup>
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-5 text-left font-medium text-slate-500 md:px-6">
                <span className="sr-only">Feature</span>
              </th>
              {PLAN_ORDER.map((key) => (
                <th
                  key={key}
                  scope="col"
                  className="px-1 py-5 text-center font-semibold text-slate-900 md:px-2"
                >
                  {PLANS[key].title}
                  {key === "pro" && (
                    <span className="mt-1 block text-[11px] font-medium uppercase tracking-wider text-violet-600">
                      Popular
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          {groups.map((group) => (
            <tbody key={group.title}>
              <tr>
                <th
                  colSpan={4}
                  scope="colgroup"
                  className="bg-white px-4 pb-2 pt-7 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-400 md:px-6"
                >
                  {group.title}
                </th>
              </tr>
              {group.rows.map((row) => (
                <tr key={row.label} className="border-t border-slate-100">
                  <th
                    scope="row"
                    className="px-4 py-3.5 text-left font-normal text-slate-700 md:px-6"
                  >
                    {row.label}
                  </th>
                  {row.values.map((value, index) => (
                    <td
                      key={PLAN_ORDER[index]}
                      className="px-1 py-3.5 text-center font-medium text-slate-900 md:px-2"
                    >
                      {renderCell(value, PLAN_ORDER[index] === "pro")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>
    </div>
  </section>
);

export default PlanComparison;
